import { useFormik } from 'formik';
import { useState } from 'react';
import {
  AgButton,
  AgGooglePlacesAutocomplete,
  AgInput,
  AgModal,
  AgModalActions,
  AgModalContent,
} from 'src/components/ui';
import {
  useCreateWarehousePlace as useCreatePlace,
  useUpdateWarehousePlace as useUpdatePlace,
} from 'src/modules/entities/places';
import type { Place } from 'types/graphql';

type Props = {
  open: boolean;
  /** The place fields the form edits; undefined for create mode. */
  warehouse?: {
    place: Pick<Place, 'id' | 'description' | 'streetAddress1' | 'city' | 'state' | 'postalCode' | 'addressCountry'>;
  };
  onClose: () => void;
};

export default function WarehouseModal({ open, warehouse, onClose }: Props) {
  const [loading, setLoading] = useState(false);
  const isEditMode = !!warehouse;

  const createPlace = useCreatePlace();
  const updatePlace = useUpdatePlace();

  const [addressInputValue, setAddressInputValue] = useState(warehouse?.place.streetAddress1 || '');

  const handleAddressSelect = (address) => {
    if (address) {
      formik.setValues({
        ...formik.values,
        streetAddress1: address.streetAddress1,
        city: address.city,
        state: address.state,
        postalCode: address.postalCode,
        addressCountry: address.addressCountry,
      });

      setAddressInputValue(address.streetAddress1);
    }
  };

  const formik = useFormik({
    initialValues: {
      warehouseName: warehouse?.place.description || '',
      streetAddress1: warehouse?.place.streetAddress1 || '',
      city: warehouse?.place.city || '',
      state: warehouse?.place.state || '',
      postalCode: warehouse?.place.postalCode || '',
      addressCountry: warehouse?.place.addressCountry || 'US',
    },
    // A warehouse is listed and picked by this name; a blank one saved a nameless warehouse.
    validate: (values) => (values.warehouseName.trim() ? {} : { warehouseName: 'Warehouse name is required.' }),
    enableReinitialize: true, // Allow form to update when warehouse prop changes
    onSubmit: async (values) => {
      setLoading(true);
      try {
        if (isEditMode) {
          await updatePlace(warehouse.place.id, {
            streetAddress1: values.streetAddress1,
            city: values.city,
            state: values.state,
            postalCode: values.postalCode,
            addressCountry: values.addressCountry,
            description: values.warehouseName,
          });
          onClose();
          return;
        }

        await createPlace({
          streetAddress1: values.streetAddress1,
          city: values.city,
          state: values.state,
          postalCode: values.postalCode,
          addressCountry: values.addressCountry,
          description: values.warehouseName,
          addressRoles: ['WAREHOUSE'],
        });
        onClose();
        formik.resetForm();
      } finally {
        setLoading(false);
      }
    },
  });

  const handleClose = () => {
    if (!loading) {
      if (!isEditMode) {
        formik.resetForm();
        setAddressInputValue('');
      }
      onClose();
    }
  };

  return (
    <AgModal open={open} onClose={handleClose} title={isEditMode ? 'Edit Warehouse' : 'Add New Warehouse'} size="md">
      <form onSubmit={formik.handleSubmit} className="flex flex-col gap-6">
        <AgModalContent>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12">
              <AgInput
                name="warehouseName"
                label="Warehouse Name"
                value={formik.values.warehouseName}
                onChange={(value) => formik.setFieldValue('warehouseName', value)}
                onBlur={() => formik.setFieldTouched('warehouseName', true)}
                isInvalid={formik.touched.warehouseName && Boolean(formik.errors.warehouseName)}
                errorMessage={formik.touched.warehouseName ? (formik.errors.warehouseName as string) : undefined}
                isRequired
              />
            </div>

            <div className="col-span-12">
              <AgGooglePlacesAutocomplete
                label="Street Address"
                value={addressInputValue}
                onChange={handleAddressSelect}
                onInputChange={(value) => {
                  setAddressInputValue(value);
                  formik.setFieldValue('streetAddress1', value);
                }}
                isInvalid={formik.touched.streetAddress1 && Boolean(formik.errors.streetAddress1)}
                errorMessage={formik.touched.streetAddress1 ? (formik.errors.streetAddress1 as string) : undefined}
                isRequired
              />
            </div>

            <div className="col-span-12 sm:col-span-6">
              <AgInput
                name="city"
                label="City"
                value={formik.values.city}
                onChange={(value) => formik.setFieldValue('city', value)}
                onBlur={() => formik.setFieldTouched('city', true)}
                isInvalid={formik.touched.city && Boolean(formik.errors.city)}
                errorMessage={formik.touched.city ? (formik.errors.city as string) : undefined}
                isRequired
              />
            </div>

            <div className="col-span-12 sm:col-span-3">
              <AgInput
                name="state"
                label="State"
                value={formik.values.state}
                onChange={(value) => formik.setFieldValue('state', value)}
                onBlur={() => formik.setFieldTouched('state', true)}
                isInvalid={formik.touched.state && Boolean(formik.errors.state)}
                errorMessage={formik.touched.state ? (formik.errors.state as string) : undefined}
                isRequired
              />
            </div>

            <div className="col-span-12 sm:col-span-3">
              <AgInput
                name="postalCode"
                label="ZIP"
                value={formik.values.postalCode}
                onChange={(value) => formik.setFieldValue('postalCode', value)}
                onBlur={() => formik.setFieldTouched('postalCode', true)}
                isInvalid={formik.touched.postalCode && Boolean(formik.errors.postalCode)}
                errorMessage={formik.touched.postalCode ? (formik.errors.postalCode as string) : undefined}
                isRequired
              />
            </div>

            <div className="col-span-12">
              <AgInput
                name="addressCountry"
                label="Country"
                value={formik.values.addressCountry}
                onChange={(value) => formik.setFieldValue('addressCountry', value)}
                onBlur={() => formik.setFieldTouched('addressCountry', true)}
                isInvalid={formik.touched.addressCountry && Boolean(formik.errors.addressCountry)}
                errorMessage={formik.touched.addressCountry ? (formik.errors.addressCountry as string) : undefined}
                isRequired
              />
            </div>
          </div>
        </AgModalContent>

        <AgModalActions>
          <AgButton variant="primary" type="submit" isDisabled={loading || !formik.isValid}>
            {loading ? (isEditMode ? 'Updating...' : 'Adding...') : isEditMode ? 'Update Warehouse' : 'Add Warehouse'}
          </AgButton>
        </AgModalActions>
      </form>
    </AgModal>
  );
}
